import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Logo } from '../../components/Redesing/logo';
import { RouterLink } from '../../components/Redesing/router-link';
import CustomToast from '../../components/Redesing/custom-toast';
import AuthLayout from '../../layout/auth/auth-layout';
import { requestAdminPasswordResetMutation } from '../../hooks/auth/mutation/useAuthMutations';
import { paths } from '../../paths';

/**
 * Seconds before the email can be requested again. ant ignores a repeat for
 * the same address inside its own one-minute window, so a shorter cooldown
 * would only pretend to resend.
 */
export const RESEND_COOLDOWN_SECONDS = 60;

interface IResetPasswordSentProps {
  email: string;
  /** Back to the form, to request the email for another address. */
  onChangeEmail: () => void;
}

/**
 * Shown after a reset email is requested. The copy is the same whether or
 * not the address belongs to an admin: ant answers every request alike, and
 * this page must not be the place that says which addresses have accounts.
 */
const ResetPasswordSent: React.FC<IResetPasswordSentProps> = ({
  email,
  onChangeEmail,
}) => {
  const [secondsUntilResend, setSecondsUntilResend] = useState(RESEND_COOLDOWN_SECONDS);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (secondsUntilResend <= 0) return undefined;
    const timer = setTimeout(() => setSecondsUntilResend((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsUntilResend]);

  const resend = async () => {
    setResending(true);
    const result = await requestAdminPasswordResetMutation(email);
    setResending(false);
    if (result.isSuccess) {
      setSecondsUntilResend(RESEND_COOLDOWN_SECONDS);
      CustomToast({ type: 'success', message: 'Sent the email again' });
    } else {
      CustomToast({
        type: 'error',
        message: 'There was an issue sending the reset email, please try again',
      });
    }
  };

  const canResend = secondsUntilResend <= 0 && !resending;

  return (
    <AuthLayout>
      <Card elevation={16}>
        <CardHeader
          subheader={
            <Box display="flex" justifyContent="center" alignItems="center">
              <Logo />
            </Box>
          }
          sx={{ pb: 0 }}
          title=""
        />
        <CardContent>
          <Stack spacing={3}>
            <Stack spacing={1.5}>
              <Typography variant="h5" component="h1">
                Check your email
              </Typography>
              <Typography variant="body1">
                If an administrator account exists for <strong>{email}</strong>, we've
                emailed it a link to reset the password. The link works for 3 days.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Didn't get the email? It can take a few minutes to arrive. Check your spam
                folder, and make sure the address above is the one your account uses.
              </Typography>
            </Stack>
            <Stack spacing={2}>
              <Button
                disabled={!canResend}
                fullWidth
                onClick={resend}
                size="large"
                variant="outlined"
              >
                {secondsUntilResend > 0
                  ? `Resend email in ${secondsUntilResend}s`
                  : 'Resend email'}
              </Button>
              <Button
                component={RouterLink}
                fullWidth
                href={paths.auth.login}
                size="large"
                variant="contained"
              >
                Back to sign in
              </Button>
            </Stack>
            <Link
              component="button"
              type="button"
              onClick={onChangeEmail}
              underline="hover"
              variant="subtitle2"
              sx={{ alignSelf: 'center' }}
            >
              Use a different email address
            </Link>
          </Stack>
        </CardContent>
      </Card>
    </AuthLayout>
  );
};

export default ResetPasswordSent;
